import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XIcon,
  CalendarDaysIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import {
  MobilePage,
} from '../components/mobile'

interface VisitGroupTemplate {
  id: string
  name: string
  description?: string | null
  recurrence_type: string
  recurrence_days: number[]
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  dsny_integration: boolean
  dsny_pickup_days?: string[]
  applicable_service_types?: string[]
  is_active: boolean
  created_at?: string
  updated_at?: string
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DSNY_DAY_OPTIONS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const emptyTemplate = (): VisitGroupTemplate => ({
  id: '',
  name: '',
  description: '',
  recurrence_type: 'weekly',
  recurrence_days: [],
  scheduled_start_time: null,
  scheduled_end_time: null,
  dsny_integration: false,
  dsny_pickup_days: [],
  applicable_service_types: [],
  is_active: true,
})

export function VisitGroupTemplates() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<VisitGroupTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [editor, setEditor] = useState<{
    isOpen: boolean
    template: VisitGroupTemplate
    isNew: boolean
    saving: boolean
  }>({
    isOpen: false,
    template: emptyTemplate(),
    isNew: true,
    saving: false,
  })

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('visit_group_templates')
        .select('*')
        .order('name', { ascending: true })
      if (error) throw error
      setTemplates(data || [])
    } catch (e) {
      console.error('Failed to fetch templates:', e)
    } finally {
      setLoading(false)
    }
  }

  const openNew = () => {
    setEditor({
      isOpen: true,
      template: emptyTemplate(),
      isNew: true,
      saving: false,
    })
  }

  const openEdit = (t: VisitGroupTemplate) => {
    setEditor({
      isOpen: true,
      template: { ...t },
      isNew: false,
      saving: false,
    })
  }

  const close = () => {
    if (editor.saving) return
    setEditor({
      isOpen: false,
      template: emptyTemplate(),
      isNew: true,
      saving: false,
    })
  }

  const updateField = (field: keyof VisitGroupTemplate, value: any) => {
    setEditor((prev) => ({
      ...prev,
      template: { ...prev.template, [field]: value },
    }))
  }

  const toggleDay = (day: number) => {
    setEditor((prev) => {
      const has = prev.template.recurrence_days.includes(day)
      return {
        ...prev,
        template: {
          ...prev.template,
          recurrence_days: has
            ? prev.template.recurrence_days.filter((d) => d !== day)
            : [...prev.template.recurrence_days, day].sort((a, b) => a - b),
        },
      }
    })
  }

  const toggleDsnyDay = (dayName: string) => {
    setEditor((prev) => {
      const days = prev.template.dsny_pickup_days || []
      const has = days.includes(dayName)
      return {
        ...prev,
        template: {
          ...prev.template,
          dsny_pickup_days: has ? days.filter((d) => d !== dayName) : [...days, dayName],
        },
      }
    })
  }

  const saveTemplate = async () => {
    const t = editor.template
    if (!t.name.trim()) {
      alert('Template name is required.')
      return
    }
    try {
      setEditor((prev) => ({ ...prev, saving: true }))
      const payload: any = {
        name: t.name.trim(),
        description: t.description || null,
        recurrence_type: t.recurrence_type || 'weekly',
        recurrence_days: t.recurrence_days || [],
        scheduled_start_time: t.scheduled_start_time || null,
        scheduled_end_time: t.scheduled_end_time || null,
        dsny_integration: !!t.dsny_integration,
        dsny_pickup_days: t.dsny_pickup_days || [],
        applicable_service_types: t.applicable_service_types || [],
        is_active: t.is_active !== false,
      }

      if (editor.isNew) {
        const { error } = await supabase.from('visit_group_templates').insert(payload)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('visit_group_templates')
          .update(payload)
          .eq('id', t.id)
        if (error) throw error
      }

      await fetchTemplates()
      close()
    } catch (e: any) {
      console.error('Failed to save template:', e)
      alert('Failed to save template: ' + (e?.message || 'Unknown error'))
    } finally {
      setEditor((prev) => ({ ...prev, saving: false }))
    }
  }

  const deleteTemplate = async (t: VisitGroupTemplate) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return
    try {
      const { error } = await supabase.from('visit_group_templates').delete().eq('id', t.id)
      if (error) throw error
      await fetchTemplates()
    } catch (e: any) {
      console.error('Failed to delete template:', e)
      alert('Failed to delete template: ' + (e?.message || 'Unknown error'))
    }
  }

  const formatDays = (t: VisitGroupTemplate) => {
    if (t.recurrence_type === 'daily') return 'Daily'
    if (t.recurrence_type === 'monthly') return 'Monthly'
    if (!t.recurrence_days || t.recurrence_days.length === 0) return '—'
    return t.recurrence_days.map((d) => DAY_LABELS[d]).join(', ')
  }

  return (
    <MobilePage>
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Visit Group Templates</h1>
            <p className="text-gray-600 mt-2">
              Reusable recurring schedule templates that can be applied to contracts.
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            New Template
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No templates yet</p>
              <p className="text-sm mt-1">
                Click "New Template" to create your first reusable visit-group setup.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recurrence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Times
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DSNY
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Tags
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {templates.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{t.name}</div>
                      {t.description && (
                        <div className="text-xs text-gray-500 mt-0.5 max-w-md truncate">
                          {t.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {t.recurrence_type?.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDays(t)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {t.scheduled_start_time && t.scheduled_end_time
                        ? `${t.scheduled_start_time.slice(0, 5)} – ${t.scheduled_end_time.slice(0, 5)}`
                        : t.scheduled_start_time
                          ? `from ${t.scheduled_start_time.slice(0, 5)}`
                          : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {t.dsny_integration ? (
                        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700">
                          {(t.dsny_pickup_days || []).map((d) => d.slice(0, 3)).join('/') || 'Yes'}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {t.applicable_service_types && t.applicable_service_types.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {t.applicable_service_types.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex px-2 py-0.5 text-[11px] rounded-full bg-gray-100 text-gray-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(t)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteTemplate(t)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {editor.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={close}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editor.isNew ? 'New Visit Group Template' : 'Edit Template'}
              </h3>
              <button
                onClick={close}
                disabled={editor.saving}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <XIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editor.template.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g., DSNY Standard - MWF"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={editor.template.description || ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Optional context for when to use this template"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recurrence Type
                  </label>
                  <select
                    value={editor.template.recurrence_type}
                    onChange={(e) => updateField('recurrence_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi_weekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom_interval">Custom Interval</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={editor.template.is_active !== false}
                      onChange={(e) => updateField('is_active', e.target.checked)}
                    />
                    Active (visible in pickers)
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recurrence Days
                </label>
                <div className="grid grid-cols-7 gap-1.5">
                  {DAY_LABELS.map((label, idx) => {
                    const selected = editor.template.recurrence_days.includes(idx)
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleDay(idx)}
                        className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                          selected
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time (optional)
                  </label>
                  <input
                    type="time"
                    value={editor.template.scheduled_start_time || ''}
                    onChange={(e) =>
                      updateField('scheduled_start_time', e.target.value || null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time (optional)
                  </label>
                  <input
                    type="time"
                    value={editor.template.scheduled_end_time || ''}
                    onChange={(e) =>
                      updateField('scheduled_end_time', e.target.value || null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!editor.template.dsny_integration}
                    onChange={(e) => updateField('dsny_integration', e.target.checked)}
                  />
                  DSNY Integration
                </label>
                {editor.template.dsny_integration && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-600 mb-1">DSNY Pickup Days</p>
                    <div className="flex flex-wrap gap-2">
                      {DSNY_DAY_OPTIONS.map((dn) => {
                        const selected = (editor.template.dsny_pickup_days || []).includes(dn)
                        return (
                          <button
                            key={dn}
                            type="button"
                            onClick={() => toggleDsnyDay(dn)}
                            className={`px-3 py-1 text-xs rounded-full border ${
                              selected
                                ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {dn.slice(0, 3)}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={(editor.template.applicable_service_types || []).join(', ')}
                  onChange={(e) =>
                    updateField(
                      'applicable_service_types',
                      e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => !!s)
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g., dsny, garbage, hallway_cleaning"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Free-form tags used to filter which templates are suggested for a given contract.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={close}
                disabled={editor.saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                disabled={editor.saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editor.saving ? 'Saving...' : editor.isNew ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
